import {
  ButtonERP,
  DataTablePrimeReact,
  OptionsMenuStorybook,
  PaginationERP,
  TableERP,
} from "@finanedi.es/storybook"
import {
  faCoins,
  faFileExcel,
  faFileXml,
} from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { FacturaReadingFiltersDtoProps } from "api/DTOs/FacturaReadingFiltersDto"
import { FacturaReadingSimpleDtoProps } from "api/DTOs/FacturaReadingSimpleDto"
import {
  deleteFacturaById,
  FacturasByFilterQueryParamsProps,
  getFacturasByFilter,
  getFacturasByIdSimple,
} from "api/Factura.service"
import { TIPO_DOCUMENTO_COMERCIAL } from "api/Types/TipoDocumentoComercial"
import { TIPO_DOCUMENTO_ENUM } from "api/Types/TipoDocumentoEnum"
import { useUsuarioEmpresaActiva } from "api/Usuario.hook"
import axios from "axios"
import { ButtonMenu } from "common/components/ButtonMenu/ButtonMenu"
import DeleteModal from "common/components/Modal/DeleteModal"
import ModalVerDocumento from "common/components/ModalVerDocumento/ModalVerDocumento"
import { STATUSCORRECT } from "common/components/ModalVerDocumento/constants"
import NoDataTemplate from "common/components/NoDataTemplate"
import { STATIC_ASSETS_URL } from "common/context/constants"
import useCrearDocumento from "common/hooks/useEnviarDocumento"
import { useRectificarFactura } from "common/hooks/useRectificarOEditarFactura"
import notification from "common/utils/notification"
import { PAGINATION_STYLES } from "constants/primereactStyles"
import PagoCobroManualForm from "features/Documentos/components/ModalPagoCobroManual/ModalPagoCobroManual"
import ModalTypeDownloadDocument from "features/Documentos/components/ModalTypeDownloadDocument/ModalTypeDownloadDocument"
import { ModalTypeDownloadDocumentProps } from "features/Documentos/components/ModalTypeDownloadDocument/constant"
import { Skeleton } from "primereact/skeleton"
import { useCallback, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"
import * as ROUTES from "routes/RoutePaths"
import { useFilterMenu } from "../../../../../hooks/useFilterMenu"
import FilterMenu from "./FilterMenu"
import { COLUMNAS_FACTURAS_ENVIADAS_ERP } from "./constants/FacturasEnviadasColumns"
import { handlerExportFacturasToExcel } from "./contants/constants"

const FacturasEnviadas = () => {
  const { t } = useTranslation()
  const [sortingCriteria, setSortingCriteria] = useState<
    FacturasByFilterQueryParamsProps["SortingCriteria"]
  >({
    sort: undefined,
    property: undefined,
  })
  const [pagination, setPagination] = useState({
    itemsPerPage: 100,
    skipCount: 0,
  })
  const [showModalDownload, setShowModalDownload] = useState<
    ModalTypeDownloadDocumentProps["modalState"]
  >({
    show: false,
    id: null,
    serie: null,
  })
  const [showModalPagoCobroManual, setShowModalPagoCobroManual] = useState<{
    show: boolean
    id: string | undefined
    serie: string | null | undefined
  }>({ show: false, id: undefined, serie: undefined })
  const [modalDocumentoDetail, setModalDocumentoDetail] = useState<{
    show: boolean
    document: FacturaReadingSimpleDtoProps | null
  }>({
    show: false,
    document: null,
  })
  const [showDeleteModal, setShowDeleteModal] = useState({
    id: "",
    show: false,
  })
  const [deleting, setDeleting] = useState(false)
  const [sortField, setSortField] = useState<string | undefined>(undefined)
  const [sortOrder, setSortOrder] = useState<1 | 0 | -1 | null | undefined>(0)
  const [isLoadingExport, setIsLoadingExport] = useState(false)

  const { rectificarOeditarFactura } = useRectificarFactura()

  const dtRef =
    useRef<DataTablePrimeReact<FacturaReadingFiltersDtoProps[]>>(null)
  const handleResetPagination = () => {
    dtRef.current && dtRef.current?.resetScroll()
    setPagination({
      itemsPerPage: pagination.itemsPerPage,
      skipCount: 0,
    })
  }
  const {
    activeCompany: { id: empresaActivaId },
  } = useUsuarioEmpresaActiva()

  const history = useHistory()

  const { crearDocumento } = useCrearDocumento()

  const enviadosCancelToken = useRef(axios.CancelToken.source())
  const fetchItems = useCallback(() => {
    return (queryState: FacturasByFilterQueryParamsProps) => {
      enviadosCancelToken.current?.cancel()
      enviadosCancelToken.current = axios.CancelToken.source()
      return getFacturasByFilter(
        {
          ...queryState,
          LibradorId: empresaActivaId,
          SortingCriteria:
            queryState.SortingCriteria ??
            "FechaEmision DESC, CreationTime DESC",
        },
        enviadosCancelToken.current.token
      )
    }
  }, [empresaActivaId])

  const { items, isLoading, totalCount, setServiceQuery, fetchTableData } =
    useFilterMenu({
      serviceCall: fetchItems,
    })

  const handleFinanciacionPanel = () => {
    history.push(ROUTES.FACTURAS_FINANCIABLES)
  }

  const showFinanciadaColumn = items.some(
    (item: FacturaReadingFiltersDtoProps) =>
      Boolean(
        item.estadoOperaciones?.estadoFinanciacionEfecto ||
          item.estadoOperaciones?.estadoOperacion
      )
  )

  const onRowClick = async ({
    data,
  }: {
    data: FacturaReadingFiltersDtoProps
  }) => {
    if (!data.id) return
    const facturaById = await getFacturasByIdSimple(data.id)
    setModalDocumentoDetail({
      show: true,
      document: facturaById,
    })
  }

  const onSort = ({
    sortField,
    sortOrder,
  }: {
    sortField: string | undefined
    sortOrder: 1 | 0 | -1 | null | undefined
  }) => {
    setSortingCriteria({
      property: sortField ?? undefined,
      sort: sortOrder === 1 ? "ASC" : sortOrder === -1 ? "DESC" : undefined,
    })
    setSortField(sortField)
    setSortOrder(sortOrder)
  }

  const toggleDeleteModal = (id?: string) => {
    setShowDeleteModal((prevState) => ({
      id: id ?? "",
      show: !prevState.show,
    }))
  }

  const deleteFactura = async () => {
    if (!showDeleteModal?.id) return
    try {
      setDeleting(true)
      const { status }: { status: number } = await deleteFacturaById(
        showDeleteModal.id
      )
      if (status === STATUSCORRECT) {
        toggleDeleteModal()
        notification.success({ body: `${t("crear.factura.delete")}` })
        fetchTableData()
      }
    } finally {
      setDeleting(false)
    }
  }

  return (
    <>
      <div className='tw-flex tw-flex-col tw-w-full tw-shadow-3x2 tw-bg-simple-10 tw-py-3.5 tw-px-8 tw-m-px sm:tw-flex-row sm:tw-justify-between sm:tw-flex-wrap tw-gap-2'>
        <div className='tw-text-xl tw-font-normal tw-flex tw-items-center tw-text-nowrap'>
          {t("crear.factura.saleLabel")} (
          {isLoading ? (
            <Skeleton className='!tw-h-5 !tw-w-8 !tw-mx-1' />
          ) : (
            <span>{totalCount}</span>
          )}
          )
        </div>
        <div className='tw-flex tw-flex-col sm:tw-flex-row sm:tw-justify-start sm:tw-flex-wrap tw-gap-1 lg:tw-gap-3'>
          <FilterMenu
            serviceQuery={setServiceQuery}
            sortingCriteria={sortingCriteria}
            maxResultCount={pagination.itemsPerPage}
            skipCount={pagination.skipCount}
            resetPagination={handleResetPagination}
          />
          <div className='tw-flex tw-flex-row tw-gap-1 lg:tw-gap-3'>
            <ButtonERP
              customize={{
                size: "md",
                type: "primary",
              }}
              onClick={() => {
                handleFinanciacionPanel()
              }}
              icon={<FontAwesomeIcon icon={faCoins} />}
              label={`${t("crear.factura.finance.requestFinanceLabel")}`}
            />

            <ButtonMenu
              onClickButton={() => crearDocumento(ROUTES.CREAR_FACTURA)}
              buttonProps={{
                children: t("crear.factura.addOrder"),
              }}
              items={[
                {
                  label: t("newDoc.facturaProforma.newInvoice"),
                  icon: "file-invoice",
                  onClick: () => crearDocumento(ROUTES.CREAR_FACTURA_PROFORMA),
                },
              ]}
            />

            <OptionsMenuStorybook
              id='facturas-enviadas'
              items={[
                {
                  title: `${t("crear.factura.importLabel")}`,
                  hasRedirect: false,
                  subMenu: [
                    {
                      title: `${t("crear.factura.importMasive")}`,
                      action: () => history.push(ROUTES.IMPORTAR_FACTURAS),
                      icon: faFileExcel,
                    },
                    {
                      title: `${t("crear.factura.importElectronica")}`,
                      action: () => history.push(ROUTES.IMPORTAR_FACTURAE),
                      icon: faFileXml,
                    },
                  ],
                },
                {
                  title: `${t("crear.factura.exportLabel")}`,
                  hasRedirect: true,
                  action: () =>
                    handlerExportFacturasToExcel(
                      TIPO_DOCUMENTO_COMERCIAL.VENTA,
                      setIsLoadingExport,
                      t
                    ),
                  isLoading: isLoadingExport,
                  disabled: isLoadingExport,
                  icon: faFileExcel,
                },
              ]}
              position='right'
            />
          </div>
        </div>
      </div>

      <div className='tw-flex tw-flex-col tw-h-[calc(100%-152px)] tw-relative '>
        <div className='tw-h-[calc(100%-60px)] tw-min-h-0'>
          <TableERP
            refTable={dtRef}
            columns={COLUMNAS_FACTURAS_ENVIADAS_ERP({
              t,
              setShowModalDownload,
              setShowModalPagoCobroManual,
              rectificarOeditarFactura,
              showFinanciadaColumn,
              toggleDeleteModal,
            })}
            className='tw-h-full !tw-z-0'
            columnResizeMode='expand'
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={onSort}
            removableSort
            value={items}
            showGridlines
            lazy
            dataKey='id'
            scrollable
            size='small'
            onRowClick={onRowClick}
            loading={isLoading}
            emptyMessage={
              <NoDataTemplate
                icon={`${STATIC_ASSETS_URL}/Imagenes/Ilustraciones/tablas/facturas-de-venta.svg`}
                title='crear.factura.saleLabel'
                description='shared.noDataDescription'
                actionLabel='crear.factura.addOrder'
                actionCommand={() => crearDocumento(ROUTES.CREAR_FACTURA)}
              />
            }
          />
        </div>
        <PaginationERP
          className={PAGINATION_STYLES}
          totalRecords={totalCount}
          first={pagination.skipCount}
          rows={pagination.itemsPerPage}
          resetScroll={() => dtRef.current && dtRef.current?.resetScroll()}
          setPagination={setPagination}
          optionDropdown={[10, 20, 50, 100]}
          texts={{
            textRowsPerPageDropdown: `${t("pagination.textRowsPag")}`,
            textCurrentPageReport: `${t("pagination.from")}`,
          }}
        />
      </div>

      <ModalTypeDownloadDocument
        modalState={showModalDownload}
        onHide={() =>
          setShowModalDownload({
            show: false,
            id: null,
            serie: null,
          })
        }
        showElements={{
          downloadPdf: true,
          downloadXml: true,
        }}
        typeDocument={TIPO_DOCUMENTO_ENUM.FACTURA}
      />
      <ModalVerDocumento
        showModal={modalDocumentoDetail.show}
        onHide={() => {
          setModalDocumentoDetail({
            show: false,
            document: null,
          })
        }}
        refreshData={fetchTableData}
        docType={TIPO_DOCUMENTO_ENUM.FACTURA}
        documentoID={modalDocumentoDetail.document?.id ?? null}
        typeDocumentTable='facturas'
        isVenta
      />
      <DeleteModal
        visible={showDeleteModal.show}
        onHide={toggleDeleteModal}
        size='small'
        iconType='remesas'
        buttonLabel={t("crear.factura.confirm")}
        title={t("crear.factura.titleDelete")}
        message={t("crear.factura.msgDelete")}
        onConfirm={deleteFactura}
        isLoading={deleting}
      />
      <PagoCobroManualForm
        show={showModalPagoCobroManual.show}
        isVenta
        onHide={() => {
          setShowModalPagoCobroManual({
            show: false,
            id: undefined,
            serie: undefined,
          })
        }}
        document={{
          serie: showModalPagoCobroManual.serie,
          id: showModalPagoCobroManual.id,
        }}
        t={t}
        updateData={fetchTableData}
      />
    </>
  )
}

export default FacturasEnviadas
